import { useState, useEffect, useMemo } from "react";
import { usePermission } from "../../../hooks/usePermission";
import { getLatestFY } from "../../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import SimpleDropdown from "../../../globalComponents/form/dropdowns/SimpleDropdown";
import Spinner from "../../../globalComponents/ui/Spinner";
import ViewOnlyBanner from "../Components/ViewOnlyBanner";
import { questionMatchesSteps } from "../Components/TagFilterUtils";
import TagFilterBar from "../Components/TagFilterBar";
import AddAssetPanel from "./AddAssetPanel";
import CopyFromFYModal from "./CopyFromFYModal";
import KpiList from "./KpiList";
import AssetList from "./AssetList";
import { useKpiAssetsData } from "./useKpiAssetsData";
import { groupAssetsAcrossKpis, safeParseJSON } from "./helpers";
import "./KpiAssets.css";

/** KPI Asset Settings page: KPI-first and asset-first views over one FY's mappings. */
export default function KPIAssetSettings() {
  const [canCreate] = usePermission("kpi-assets.create", "KPI_ASSETS");
  const [canUpdate] = usePermission("kpi-assets.update", "KPI_ASSETS");
  const [canDelete] = usePermission("kpi-assets.delete", "KPI_ASSETS");

  /** Financial years come from localStorage once, at mount. */
  const [fyData] = useState(() => safeParseJSON(localStorage.getItem("financialYears"), []));
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);
  const isViewOnly = selectedFY?.id !== latestFY?.id;

  const { allQuestions, kpiAssetMap, sources, loading, refreshing, error, refresh, reload } =
    useKpiAssetsData(selectedFY?.id);

  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [kpiView, setKpiView] = useState("list");
  /** Row grouping: "kpi" | "asset". */
  const [groupBy, setGroupBy] = useState("kpi");
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyResult, setCopyResult] = useState(null);

  const handleFYChange = (val) => {
    const found = fyData.find(f => String(f.id) === String(val));
    if (!found) return;
    setSelectedFY(found);
    setFilterSteps([]);
    setSearch("");
    setShowAddAsset(false);
  };

  /**
   * The page-level tag filter bar is hidden in the asset view — cleared on
   * the way in so no invisible filtering applies to the groups.
   */
  const handleGroupByChange = (next) => {
    setGroupBy(next);
    if (next !== "asset") setShowAddAsset(false);
    if (next === "asset") setFilterSteps([]);
  };

  useEffect(() => {
    if (!copyResult) return;
    const t = setTimeout(() => setCopyResult(null), 6000);
    return () => clearTimeout(t);
  }, [copyResult]);

  const fyOptions = useMemo(() =>
    fyData.map(fy => ({ value: fy.id, label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}` })),
    [fyData, latestFY]
  );

  /**
   * Tag filter applies in both groupings; search targets KPI titles in the KPI
   * view but asset titles in the asset view, so it's applied separately.
   */
  const stepFilteredQuestions = useMemo(
    () => allQuestions.filter(q => questionMatchesSteps(q, filterSteps)),
    [allQuestions, filterSteps]
  );

  const filteredQuestions = useMemo(() => {
    if (!search.trim()) return stepFilteredQuestions;
    const s = search.toLowerCase();
    return stepFilteredQuestions.filter(q =>
      (q.title || "").toLowerCase().includes(s) || (q.moduleName || "").toLowerCase().includes(s));
  }, [stepFilteredQuestions, search]);

  const kpisWithAssets = useMemo(() =>
    filteredQuestions.filter(q => (kpiAssetMap[q.questionId] || []).some(a => a.isLocationRequired)),
    [filteredQuestions, kpiAssetMap]
  );

  const kpisWithGlobalAssets = useMemo(() =>
    filteredQuestions.filter(q => (kpiAssetMap[q.questionId] || []).some(a => !a.isLocationRequired)),
    [filteredQuestions, kpiAssetMap]
  );

  const displayList =
    kpiView === "with-assets" ? kpisWithAssets
      : kpiView === "with-sub-kpis" ? kpisWithGlobalAssets
        : filteredQuestions;

  /** KPI candidates for the asset-view "Add Asset" panel. */
  const addAssetCandidates = useMemo(() =>
    allQuestions.map(q => ({
      kpiId: q.questionId,
      kpiTitle: q.title,
      tags: q.tags,
      assets: kpiAssetMap[q.questionId] || [],
    })),
    [allQuestions, kpiAssetMap]
  );

  /**
   * One group per distinct asset (title + scope + unit) with the KPIs holding
   * it — drives the asset-first view and its cross-KPI rename/delete flows.
   */
  const assetGroups = useMemo(
    () => groupAssetsAcrossKpis(allQuestions, kpiAssetMap),
    [allQuestions, kpiAssetMap]
  );

  /**
   * Asset-view filtering: tag filter passes a group when any member KPI
   * passes; search matches the asset title.
   */
  const filteredGroups = useMemo(() => {
    const visibleKpiIds = new Set(stepFilteredQuestions.map(q => q.questionId));
    let gs = assetGroups.filter(g => g.members.some(m => visibleKpiIds.has(m.kpiId)));
    if (search.trim()) {
      const s = search.toLowerCase();
      gs = gs.filter(g => (g.asset.assetTitle || "").toLowerCase().includes(s));
    }
    return gs;
  }, [assetGroups, stepFilteredQuestions, search]);

  const locationGroups = useMemo(
    () => filteredGroups.filter(g => !!g.asset.isLocationRequired),
    [filteredGroups]
  );

  const globalGroups = useMemo(
    () => filteredGroups.filter(g => !g.asset.isLocationRequired),
    [filteredGroups]
  );

  const displayGroups =
    kpiView === "with-assets" ? locationGroups
      : kpiView === "with-sub-kpis" ? globalGroups
        : filteredGroups;

  if (loading) {
    return (
      <div className="kpa-root">
        <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          <Spinner size={32} color="var(--kpa-primary)" />
          <span style={{ fontSize: 13, color: "var(--kpa-slate-400)" }}>Loading KPI Assets…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="kpa-root">
      <div className="kpa-topbar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, var(--kpa-primary), #2563eb)" }} aria-hidden="true" />
          <h1 style={{ fontSize: 14, fontWeight: 700, color: "var(--kpa-slate-900)", margin: 0 }}>KPI Asset Settings</h1>
          {refreshing && <Spinner size={14} color="var(--kpa-primary)" />}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {!isViewOnly && canCreate && groupBy === "asset" && (
            <button
              type="button"
              className="kpa-topbar-btn"
              onClick={() => setShowAddAsset(s => !s)}
              title="Create an asset and pick the KPIs it belongs to"
            >
              <i className={`fas ${showAddAsset ? "fa-times" : "fa-plus"}`} style={{ fontSize: 11 }} aria-hidden="true" />
              {showAddAsset ? "Close Add Asset" : "Add Asset"}
            </button>
          )}
          {fyData.length > 1 && (
            <button
              type="button"
              className="kpa-topbar-btn"
              onClick={() => setShowCopyModal(true)}
              title="Copy KPI asset mappings from another financial year"
            >
              <i className="fas fa-copy" style={{ fontSize: 11 }} aria-hidden="true" />
              Copy from FY
            </button>
          )}
          {fyData.length > 0 && (
            <SimpleDropdown
              label="Financial Year"
              value={selectedFY?.id ?? ""}
              onChange={handleFYChange}
              options={fyOptions}
              isAlert={isViewOnly}
            />
          )}
        </div>
      </div>

      <div style={{ padding: 24, maxWidth: 1040, margin: "0 auto" }}>

        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        {error && (
          <div className="kpa-banner-error" role="alert">
            <span>
              <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} aria-hidden="true" />
              {error}
            </span>
            <button type="button" className="kpa-btn kpa-btn--ghost" onClick={reload}>
              <i className="fas fa-rotate-right" style={{ marginRight: 4 }} aria-hidden="true" />Retry
            </button>
          </div>
        )}

        {copyResult && (
          <div className="kpa-banner-success" role="status">
            <span style={{ fontSize: 12, color: "var(--kpa-green-800)", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
              <i className="fas fa-check-circle" aria-hidden="true" />
              Copied {copyResult.copied} · Skipped {copyResult.skipped} · Overwritten {copyResult.overwritten}
            </span>
            <button type="button" className="kpa-dismiss-btn" onClick={() => setCopyResult(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div className="kpa-tabs" role="tablist" aria-label={groupBy === "asset" ? "Asset views" : "KPI views"}>
            {(groupBy === "asset"
              ? [
                { key: "list", label: "All Assets", count: filteredGroups.length, countClass: "kpa-tab-count--list" },
                { key: "with-assets", label: "Assets", count: locationGroups.length, countClass: "kpa-tab-count--assets" },
                { key: "with-sub-kpis", label: "Global Assets", count: globalGroups.length, countClass: "kpa-tab-count--globals" },
              ]
              : [
                { key: "list", label: "All KPIs", count: filteredQuestions.length, countClass: "kpa-tab-count--list" },
                { key: "with-assets", label: "Assets", count: kpisWithAssets.length, countClass: "kpa-tab-count--assets" },
                { key: "with-sub-kpis", label: "Global Assets", count: kpisWithGlobalAssets.length, countClass: "kpa-tab-count--globals" },
              ]
            ).map(tab => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={kpiView === tab.key}
                className={`kpa-tab${kpiView === tab.key ? " kpa-tab--active" : ""}`}
                onClick={() => setKpiView(tab.key)}
              >
                {tab.label}
                <span className={`kpa-tab-count ${tab.countClass}`}>{tab.count}</span>
              </button>
            ))}
          </div>

          <div className="kpa-tabs" role="tablist" aria-label="Group rows by">
            {[
              { key: "kpi", label: "By KPI" },
              { key: "asset", label: "By Asset" },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={groupBy === opt.key}
                className={`kpa-tab${groupBy === opt.key ? " kpa-tab--active" : ""}`}
                onClick={() => handleGroupByChange(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="kpa-card" style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ position: "relative", marginBottom: groupBy === "asset" ? 0 : 12 }}>
            <i className="fas fa-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--kpa-slate-400)", pointerEvents: "none" }} aria-hidden="true" />
            <input
              className="kpa-search-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={groupBy === "asset" ? "Search by asset title" : "Search by title or module"}
              aria-label={groupBy === "asset" ? "Search assets by title" : "Search KPIs by title or module"}
            />
          </div>
          {groupBy !== "asset" && (
            <TagFilterBar
              allQuestions={allQuestions}
              filterSteps={filterSteps}
              onFilterStepsChange={setFilterSteps}
              pendingIds={new Set(allQuestions.map(q => q.questionId))}
              disabled={isViewOnly}
            />
          )}
        </div>

        {showAddAsset && groupBy === "asset" && !isViewOnly && canCreate && (
          <AddAssetPanel
            withKpiPicker
            pagePanel
            candidates={addAssetCandidates}
            selectedFYId={selectedFY?.id}
            sources={sources}
            onSaved={refresh}
            onClose={() => setShowAddAsset(false)}
          />
        )}

        <div className="kpa-card" style={{ overflow: "hidden" }}>
          {groupBy === "asset" ? (
            <AssetList
              displayGroups={displayGroups}
              kpiView={kpiView}
              allQuestions={allQuestions}
              kpiAssetMap={kpiAssetMap}
              sources={sources}
              selectedFYId={selectedFY?.id}
              onSaved={refresh}
              isViewOnly={isViewOnly}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          ) : (
            <KpiList
              displayList={displayList}
              kpiView={kpiView}
              kpiAssetMap={kpiAssetMap}
              sources={sources}
              selectedFYId={selectedFY?.id}
              onSaved={refresh}
              isViewOnly={isViewOnly}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          )}
        </div>

      </div>

      {showCopyModal && (
        <CopyFromFYModal
          fyData={fyData}
          allQuestions={allQuestions}
          targetFy={selectedFY}
          onClose={() => setShowCopyModal(false)}
          onCopied={(summary) => {
            setShowCopyModal(false);
            setCopyResult(summary);
            refresh();
          }}
        />
      )}
    </div>
  );
}
